import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/compras/pedido-compra-item-sheet.tsx — Fase 13, Plano 19 (COMP-02, D-20).
// FormDialog lateral (1 coluna, sm:max-w-lg) de inclusão/edição de item do pedido de compra: Matéria-prima *, Código do
// fornecedor (pelo vínculo), Quantidade * na unidade de compra com "= {qtd} {un estoque}", Unidade de compra (somente
// leitura), Preço unitário * e Prazo de entrega. Sem fator de conversão: alerta amber (o pedido é salvo, o recebimento não).
import { useEffect, useState, type FormEvent } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { MaterialLookupCompras } from '@pieltec/contracts';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { MaterialCombobox } from '../../components/vendas/material-combobox';
import {
  ALERTA_SEM_FATOR,
  ORDEM_ITEM_PEDIDO,
  bloqueioItemComRecebimento,
  fatorEfetivo,
  lerNumero,
  materialSemFator,
  textoConversao,
  unidadeDeCompra,
  validarItemPedidoCompra,
  type CampoItemPedido,
  type ErrosItemPedido,
  type ItemPedidoCompraForm,
} from '../../lib/compras/pedido-form';
import { paraTexto, paraTextoPreco } from '../../lib/vendas/numero';

const ENDPOINT_MATERIAIS = '/api/v1/compras/lookups/materiais';

const ID_CAMPO: Record<CampoItemPedido, string> = {
  material: 'pc-item-material',
  quantidade: 'pc-item-quantidade',
  preco: 'pc-item-preco',
};

interface PedidoCompraItemSheetProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  /** Fornecedor do pedido: o lookup devolve o código do material nele (vínculo). */
  readonly fornecedorId: string | null;
  /** Item em edição (null = novo item). */
  readonly inicial: ItemPedidoCompraForm | null;
  /** Posição (1-based) do item em edição, para as mensagens de bloqueio; ignorado em item novo. */
  readonly numeroItem?: number;
  /** Prazo geral do pedido: o item novo nasce com ele (o usuário pode ajustar item a item). */
  readonly prazoGeral: string;
  /** `continuar` = "Adicionar e continuar": o FormDialog permanece aberto com os campos limpos. */
  readonly onSalvar: (item: ItemPedidoCompraForm, continuar: boolean) => void;
}

function focarCampo(id: string | undefined): void {
  if (!id) return;
  window.requestAnimationFrame(() => document.getElementById(id)?.focus());
}

export function PedidoCompraItemSheet({ aberto, onAbertoChange, fornecedorId, inicial, numeroItem = 1, prazoGeral, onSalvar }: PedidoCompraItemSheetProps) {
  const [material, setMaterial] = useState<MaterialLookupCompras | null>(null);
  const [codigo, setCodigo] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [preco, setPreco] = useState('');
  const [prazo, setPrazo] = useState('');
  const [erros, setErros] = useState<ErrosItemPedido>({});

  useEffect(() => {
    if (!aberto) return;
    setMaterial(inicial?.material ?? null);
    setCodigo(inicial?.codigoFornecedor ?? '');
    setQuantidade(inicial ? paraTexto(inicial.quantidade) : '');
    setPreco(inicial ? paraTextoPreco(inicial.precoUnitario) : '');
    setPrazo(inicial ? (inicial.prazoEntrega ?? '') : prazoGeral);
    setErros({});
    // O FormDialog só reinicia quando abre ou troca o item; mudar o prazo geral com o FormDialog aberto não deve apagar o que foi digitado.
  }, [aberto, inicial]);

  const comRecebimento = (inicial?.recebido ?? 0) > 0;
  const quantidadeDigitada = lerNumero(quantidade);
  const conversao = material ? textoConversao(quantidadeDigitada, fatorEfetivo(material), material.unidadeEstoque) : null;

  function escolherMaterial(escolhido: MaterialLookupCompras): void {
    setMaterial(escolhido);
    setCodigo(escolhido.codigoFornecedor ?? '');
    setErros((atuais) => ({ ...atuais, material: undefined }));
  }

  function validar(): ItemPedidoCompraForm | null {
    const proximos = validarItemPedidoCompra({ materialId: material?.id, quantidadeTexto: quantidade, precoTexto: preco });
    const itemProposto =
      material && Number.isFinite(quantidadeDigitada) && quantidadeDigitada > 0 ? { material, quantidade: quantidadeDigitada } : null;
    if (inicial && itemProposto && !proximos.material && !proximos.quantidade) {
      const bloqueio = bloqueioItemComRecebimento(numeroItem, inicial, itemProposto);
      if (bloqueio) proximos.quantidade = bloqueio;
    }
    setErros(proximos);
    const invalido = ORDEM_ITEM_PEDIDO.find((campo) => proximos[campo]);
    if (invalido) {
      focarCampo(ID_CAMPO[invalido]);
      return null;
    }
    if (!material) return null;
    return {
      ...(inicial?.id ? { id: inicial.id } : {}),
      material,
      codigoFornecedor: codigo.trim() || null,
      quantidade: quantidadeDigitada,
      precoUnitario: lerNumero(preco),
      prazoEntrega: prazo || null,
      ...(inicial?.recebido !== undefined ? { recebido: inicial.recebido } : {}),
    };
  }

  function salvar(continuar: boolean): void {
    const item = validar();
    if (!item) return;
    onSalvar(item, continuar);
    if (continuar) {
      setMaterial(null);
      setCodigo('');
      setQuantidade('');
      setPreco('');
      setPrazo(prazoGeral);
      setErros({});
      focarCampo(ID_CAMPO.material);
    }
  }

  function aoEnviar(event: FormEvent): void {
    event.preventDefault();
    salvar(false);
  }

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent>
        <form onSubmit={aoEnviar} noValidate className="flex h-full flex-col">
          <FormDialogHeader>
            <FormDialogTitle className="text-xl">{inicial ? 'Editar item' : 'Adicionar item'}</FormDialogTitle>
            <FormDialogDescription>Informe a matéria-prima e a quantidade na unidade de compra.</FormDialogDescription>
          </FormDialogHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ID_CAMPO.material}>Matéria-prima *</Label>
              <MaterialCombobox<MaterialLookupCompras>
                id={ID_CAMPO.material}
                value={material}
                onChange={escolherMaterial}
                endpoint={ENDPOINT_MATERIAIS}
                parametros={fornecedorId ? { fornecedorId } : undefined}
                invalido={!!erros.material}
                disabled={comRecebimento}
              />
              {comRecebimento && <p className="text-sm text-muted-foreground">Este item já tem recebimento: a matéria-prima não pode ser trocada.</p>}
              {erros.material && <FormMessage>{erros.material}</FormMessage>}
            </div>

            {material && materialSemFator(material) && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle aria-hidden="true" />
                <AlertDescription className="text-amber-900">{ALERTA_SEM_FATOR}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pc-item-codigo">Código do fornecedor</Label>
              <Input id="pc-item-codigo" value={codigo} onChange={(e) => setCodigo(e.target.value)} maxLength={60} aria-describedby="pc-item-codigo-ajuda" />
              <p id="pc-item-codigo-ajuda" className="text-sm text-muted-foreground">
                Preenchido pelo vínculo com o fornecedor, quando existe.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={ID_CAMPO.quantidade}>Quantidade *</Label>
                <Input
                  id={ID_CAMPO.quantidade}
                  inputMode="decimal"
                  value={quantidade}
                  onChange={(e) => setQuantidade(e.target.value)}
                  className="text-right tabular-nums"
                  aria-invalid={erros.quantidade ? true : undefined}
                  aria-describedby="pc-item-conversao"
                />
                <p id="pc-item-conversao" className="min-h-5 text-sm text-muted-foreground tabular-nums" aria-live="polite">
                  {conversao ?? ' '}
                </p>
                {erros.quantidade && <FormMessage>{erros.quantidade}</FormMessage>}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pc-item-unidade">Unidade de compra</Label>
                <Input id="pc-item-unidade" value={material ? unidadeDeCompra(material) : ''} readOnly tabIndex={-1} className="bg-slate-50" />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ID_CAMPO.preco}>Preço unitário *</Label>
              <Input
                id={ID_CAMPO.preco}
                inputMode="decimal"
                value={preco}
                onChange={(e) => setPreco(e.target.value)}
                className="text-right tabular-nums"
                aria-invalid={erros.preco ? true : undefined}
                aria-describedby="pc-item-preco-ajuda"
              />
              <p id="pc-item-preco-ajuda" className="text-sm text-muted-foreground">
                R$ por unidade de compra{material ? ` (${unidadeDeCompra(material)})` : ''}.
              </p>
              {erros.preco && <FormMessage>{erros.preco}</FormMessage>}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pc-item-prazo">Prazo de entrega</Label>
              <DatePicker id="pc-item-prazo" value={prazo} onValueChange={(value) => setPrazo(value)} />
            </div>
          </div>

          <FormDialogFooter className="border-t sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onAbertoChange(false)}>
              Voltar
            </Button>
            {!inicial && (
              <Button type="button" variant="secondary" onClick={() => salvar(true)}>
                Adicionar e continuar
              </Button>
            )}
            <Button type="submit">
              {inicial ? 'Salvar item' : 'Adicionar item'}
            </Button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
}
